import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { NavController } from '@ionic/angular';
import { Observable, Subject, tap, map, of, catchError, throwError } from 'rxjs';
import { environment } from '../../environments/environment';

const STORAGE_KEY_TOKEN = 'business_auth_token';
const STORAGE_KEY_ACCOUNT = 'business_account';
const STORAGE_KEY_ACCOUNT_TYPE = 'business_account_type';
const STORAGE_KEY_ACTIVE_BUSINESS_ID = 'business_active_account_id';
const STORAGE_KEY_MASTER_COMPANIES = 'business_master_companies';
const STORAGE_KEY_MASTER_CHOOSER_NOTICE = 'business_master_chooser_notice';

export interface MasterCompanyChooserNotice {
  title: string;
  message: string;
  previousCompanyId?: string;
}

export type BusinessSessionAccountType = 'business' | 'business_master';

export interface BusinessMasterCompanySummary {
  id: string;
  tradeName?: string;
  legalName?: string;
  rfc?: string;
  abbreviation?: string;
}

/**
 * `localStorage` se comparte entre pestañas; `sessionStorage` no. Sin esto, Cmd+clic
 * en enlaces de empresas abre una pestaña sin token y el guard manda al login.
 */
function readBusinessStorage(key: string): string | null {
  const fromLocal = localStorage.getItem(key);
  if (fromLocal) return fromLocal;
  const fromSession = sessionStorage.getItem(key);
  if (fromSession) {
    localStorage.setItem(key, fromSession);
    sessionStorage.removeItem(key);
    return fromSession;
  }
  return null;
}

function writeBusinessStorage(key: string, value: string): void {
  localStorage.setItem(key, value);
  sessionStorage.removeItem(key);
}

function clearBusinessStorageKey(key: string): void {
  localStorage.removeItem(key);
  sessionStorage.removeItem(key);
}

export interface BusinessAccount {
  id: string;
  isCompanyAdmin?: boolean;
  canGenerateCompanyCodes?: boolean;
  requireLinkedPassengers?: boolean;
  requireBeneficiaryEmployeeNumber?: boolean;
  companyRoot?: {
    account?: BusinessAccount | null;
    primaryPassenger?: {
      id?: string | null;
      userId?: string | null;
    } | null;
  } | null;
  primaryPassenger?: {
    id?: string | null;
    userId?: string | null;
  } | null;
  email: string;
  contactName: string;
  contactPhone: string;
  tradeName: string;
  legalName: string;
  industry: string;
  rfc: string;
  fiscalStreet: string;
  fiscalExteriorNumber: string;
  fiscalInteriorNumber: string;
  fiscalColony: string;
  fiscalPostalCode: string;
  fiscalCity: string;
  fiscalState: string;
  taxRegime: string;
  cfdiUsage: string;
  lastLoginAt: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BusinessLoginRequest {
  email: string;
  password: string;
}

export interface BusinessLoginResponse {
  success: boolean;
  message: string;
  access_token: string;
  businessAccount: BusinessAccount;
  accountType?: BusinessSessionAccountType;
}

export interface BusinessMasterLoginResponse {
  success: boolean;
  message?: string;
  access_token: string;
  accountType?: BusinessSessionAccountType;
  masterAccount?: { id: string; email?: string; name?: string };
  companies?: BusinessMasterCompanySummary[];
}

export interface BusinessMasterCompaniesResponse {
  items: BusinessMasterCompanySummary[];
  total?: number;
}

export interface BusinessChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface BusinessForgotPasswordRequest {
  email: string;
}

export interface BusinessValidateResetTokenRequest {
  token: string;
}

export interface BusinessResetPasswordRequest {
  token: string;
  newPassword: string;
  confirmPassword: string;
}

@Injectable({
  providedIn: 'root',
})
export class BusinessAuthService {
  /** Emitido tras cambiar la empresa activa (sesión maestra). */
  readonly activeCompanyChanged$ = new Subject<string>();

  private readonly navCtrl = inject(NavController);

  /** Respaldo en memoria si la navegación no recarga la app (sessionStorage sigue siendo la fuente tras reload). */
  private masterChooserNoticePending: MasterCompanyChooserNotice | null = null;

  constructor(
    private http: HttpClient,
    private router: Router,
  ) {}

  /** Limpia el stack de Ionic y entra al panel (login dejaba la vista anterior encima). */
  enterBusinessPanel(): void {
    this.navigateRootWithHardFallback('/empresas/panel', 'app-business-dashboard-full');
  }

  enterMasterCompanyChooser(): void {
    this.navigateRootWithHardFallback('/empresas/elegir-empresa', 'app-business-choose-company');
  }

  /** Aviso one-shot al entrar en /empresas/elegir-empresa tras 403/400 de empresa activa. */
  setMasterCompanyChooserNotice(notice: MasterCompanyChooserNotice): void {
    this.masterChooserNoticePending = notice;
    sessionStorage.setItem(STORAGE_KEY_MASTER_CHOOSER_NOTICE, JSON.stringify(notice));
  }

  consumeMasterCompanyChooserNotice(): MasterCompanyChooserNotice | null {
    const pending = this.masterChooserNoticePending;
    this.masterChooserNoticePending = null;

    const raw = sessionStorage.getItem(STORAGE_KEY_MASTER_CHOOSER_NOTICE);
    sessionStorage.removeItem(STORAGE_KEY_MASTER_CHOOSER_NOTICE);

    if (pending) {
      return pending;
    }
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as MasterCompanyChooserNotice;
    } catch {
      return null;
    }
  }

  /** Empresa activa inválida o no asignada: limpia contexto y abre selector. */
  redirectToMasterCompanyChooser(notice?: Partial<MasterCompanyChooserNotice>): void {
    const previousCompanyId =
      notice?.previousCompanyId?.trim() ||
      this.getActiveBusinessAccountId()?.trim() ||
      this.getBusinessAccount()?.id?.trim() ||
      undefined;

    this.setMasterCompanyChooserNotice({
      title: notice?.title?.trim() || 'Sin acceso a esta empresa',
      message:
        notice?.message?.trim() ||
        'La empresa que tenías seleccionada ya no está asignada a tu cuenta maestra. Elige otra de la lista.',
      previousCompanyId,
    });

    clearBusinessStorageKey(STORAGE_KEY_ACCOUNT);
    clearBusinessStorageKey(STORAGE_KEY_ACTIVE_BUSINESS_ID);
    this.enterMasterCompanyChooser();
  }

  masterCompanyAccessErrorMessage(error: HttpErrorResponse): string {
    const body = error.error as { message?: string | string[] } | undefined;
    const raw = body?.message;
    if (Array.isArray(raw)) {
      return String(raw[0] ?? '').trim();
    }
    if (typeof raw === 'string') {
      return raw.trim();
    }
    return '';
  }

  /** 400/403 del API maestra (header faltante o empresa no asignada). */
  isMasterCompanyAccessHttpError(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }
    if (error.status !== 400 && error.status !== 403) {
      return false;
    }
    const body = error.error as { message?: string | string[] } | undefined;
    const raw = body?.message;
    const msg = (Array.isArray(raw) ? raw[0] : raw) ?? '';
    const msgLower = String(msg).toLowerCase();
    if (error.status === 400) {
      return (
        msgLower.includes('x-business-account-id') ||
        msgLower.includes('business account id') ||
        msgLower.includes('empresa activa') ||
        msgLower.includes('selecciona una empresa')
      );
    }
    return (
      msgLower.includes('asignada') ||
      msgLower.includes('cuenta maestra') ||
      msgLower.includes('no está asignada')
    );
  }

  private navigateRootWithHardFallback(url: string, expectedSelector: string): void {
    const hardFallback = () => window.location.replace(url);
    void this.navCtrl.navigateRoot(url, { animated: false }).then((ok) => {
      if (!ok) {
        hardFallback();
        return;
      }
      if (typeof document === 'undefined') {
        return;
      }
      requestAnimationFrame(() => {
        const onLogin =
          document.querySelector('app-business-login') != null ||
          window.location.pathname.includes('/empresas/iniciar-sesion');
        const hasTarget = document.querySelector(expectedSelector) != null;
        if (onLogin || !hasTarget) {
          hardFallback();
        }
      });
    });
  }

  login(credentials: BusinessLoginRequest): Observable<BusinessLoginResponse> {
    const url = `${environment.apiUrl}/auth/business-login`;
    return this.http.post<BusinessLoginResponse>(url, credentials).pipe(
      tap((res) => {
        this.clearMasterSessionStorage();
        if (res.access_token) {
          writeBusinessStorage(STORAGE_KEY_TOKEN, res.access_token);
        }
        writeBusinessStorage(STORAGE_KEY_ACCOUNT_TYPE, 'business');
        if (res.businessAccount) {
          writeBusinessStorage(STORAGE_KEY_ACCOUNT, JSON.stringify(res.businessAccount));
        }
      }),
    );
  }

  loginMaster(credentials: BusinessLoginRequest): Observable<BusinessMasterLoginResponse> {
    const url = `${environment.apiUrl}/auth/business-master-login`;
    return this.http.post<BusinessMasterLoginResponse>(url, credentials).pipe(
      tap((res) => {
        clearBusinessStorageKey(STORAGE_KEY_ACCOUNT);
        clearBusinessStorageKey(STORAGE_KEY_ACTIVE_BUSINESS_ID);
        if (res.access_token) {
          writeBusinessStorage(STORAGE_KEY_TOKEN, res.access_token);
        }
        writeBusinessStorage(STORAGE_KEY_ACCOUNT_TYPE, 'business_master');
        const companies = extractMasterCompaniesFromLoginResponse(res);
        writeBusinessStorage(STORAGE_KEY_MASTER_COMPANIES, JSON.stringify(companies));
        if (companies.length === 1) {
          writeBusinessStorage(STORAGE_KEY_ACTIVE_BUSINESS_ID, companies[0].id);
        }
      }),
    );
  }

  /** GET /business/master/companies — sin X-Business-Account-Id. */
  fetchMasterCompanies(): Observable<BusinessMasterCompanySummary[]> {
    const url = `${environment.apiUrl}/business/master/companies`;
    return this.http.get<BusinessMasterCompaniesResponse | BusinessMasterCompanySummary[]>(url).pipe(
      map((res) => {
        if (Array.isArray(res)) return normalizeMasterCompanies(res);
        return normalizeMasterCompanies(res.items);
      }),
      tap((items) => {
        writeBusinessStorage(STORAGE_KEY_MASTER_COMPANIES, JSON.stringify(items));
      }),
    );
  }

  getMasterCompanies(): BusinessMasterCompanySummary[] {
    const raw = readBusinessStorage(STORAGE_KEY_MASTER_COMPANIES);
    if (!raw) return [];
    try {
      return normalizeMasterCompanies(JSON.parse(raw) as unknown);
    } catch {
      return [];
    }
  }

  masterCompanyLabel(c: BusinessMasterCompanySummary): string {
    const trade = (c.tradeName || '').trim();
    const legal = (c.legalName || '').trim();
    const abbr = (c.abbreviation || '').trim();
    const base = trade || legal || c.id;
    return abbr ? `${base} (${abbr})` : base;
  }

  isMasterSession(): boolean {
    return readBusinessStorage(STORAGE_KEY_ACCOUNT_TYPE) === 'business_master';
  }

  isBusinessSession(): boolean {
    const t = readBusinessStorage(STORAGE_KEY_ACCOUNT_TYPE);
    return !t || t === 'business';
  }

  getActiveBusinessAccountId(): string | null {
    return readBusinessStorage(STORAGE_KEY_ACTIVE_BUSINESS_ID);
  }

  /** Tras login maestra o en pantalla elegir empresa. */
  setActiveBusinessAccountId(id: string): void {
    writeBusinessStorage(STORAGE_KEY_ACTIVE_BUSINESS_ID, id.trim());
  }

  needsMasterCompanySelection(): boolean {
    return this.isMasterSession() && !this.getActiveBusinessAccountId()?.trim();
  }

  /**
   * Cambia empresa activa, recarga GET /business/account y notifica al panel.
   * Valida que la empresa esté en la lista asignada.
   */
  switchActiveCompany(companyId: string): Observable<BusinessAccount> {
    const id = companyId.trim();
    const allowed = this.getMasterCompanies().some((c) => c.id === id);
    if (!allowed) {
      throw new Error('Empresa no asignada a esta cuenta maestra');
    }
    clearBusinessStorageKey(STORAGE_KEY_ACCOUNT);
    this.setActiveBusinessAccountId(id);
    return this.fetchAccount().pipe(
      tap((account) => {
        this.activeCompanyChanged$.next(id);
        if (account?.id && account.id !== id) {
          console.warn('BusinessAuth: active company id mismatch after fetchAccount');
        }
      }),
    );
  }

  /** Tras login maestra con una sola empresa o al confirmar en elegir-empresa. */
  completeMasterCompanySelection(companyId: string): Observable<BusinessAccount> {
    return this.switchActiveCompany(companyId);
  }

  forgotPassword(body: BusinessForgotPasswordRequest): Observable<{ success?: boolean; message?: string }> {
    const url = `${environment.apiUrl}/auth/business/forgot-password`;
    return this.http.post<{ success?: boolean; message?: string }>(url, body);
  }

  validateResetToken(body: BusinessValidateResetTokenRequest): Observable<{ success?: boolean; message?: string }> {
    const url = `${environment.apiUrl}/auth/business/validate-reset-token`;
    return this.http.post<{ success?: boolean; message?: string }>(url, body);
  }

  resetPassword(body: BusinessResetPasswordRequest): Observable<{ success?: boolean; message?: string }> {
    const url = `${environment.apiUrl}/auth/business/reset-password`;
    return this.http.post<{ success?: boolean; message?: string }>(url, body);
  }

  logout(): void {
    clearBusinessStorageKey(STORAGE_KEY_TOKEN);
    clearBusinessStorageKey(STORAGE_KEY_ACCOUNT);
    this.clearMasterSessionStorage();
    this.router.navigateByUrl('/empresas/iniciar-sesion', { replaceUrl: true });
  }

  private clearMasterSessionStorage(): void {
    clearBusinessStorageKey(STORAGE_KEY_ACCOUNT_TYPE);
    clearBusinessStorageKey(STORAGE_KEY_ACTIVE_BUSINESS_ID);
    clearBusinessStorageKey(STORAGE_KEY_MASTER_COMPANIES);
  }

  getToken(): string | null {
    return readBusinessStorage(STORAGE_KEY_TOKEN);
  }

  getBusinessAccount(): BusinessAccount | null {
    const raw = readBusinessStorage(STORAGE_KEY_ACCOUNT);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as BusinessAccount;
    } catch {
      return null;
    }
  }

  /** GET /api/v1/business/account - empresa activa (JWT normal o maestra + header). */
  fetchAccount(): Observable<BusinessAccount> {
    const url = `${environment.apiUrl}/business/account`;
    return this.http.get<BusinessAccount | { data: BusinessAccount }>(url).pipe(
      map((res) => ('data' in res && res.data ? res.data : res) as BusinessAccount),
      tap((account) => {
        if (account?.id) {
          writeBusinessStorage(STORAGE_KEY_ACCOUNT, JSON.stringify(account));
          if (this.isMasterSession()) {
            this.setActiveBusinessAccountId(account.id);
          }
        }
      }),
      catchError((err: HttpErrorResponse) => {
        if (this.isMasterSession() && this.isMasterCompanyAccessHttpError(err)) {
          const backendMsg = this.masterCompanyAccessErrorMessage(err);
          this.redirectToMasterCompanyChooser({
            message:
              backendMsg ||
              'Esta empresa no está asignada a la cuenta maestra. Elige otra empresa de la lista.',
          });
        }
        return throwError(() => err);
      }),
    );
  }

  /** PATCH /api/v1/business/account/password — no disponible para sesión maestra. */
  changePassword(body: BusinessChangePasswordRequest): Observable<unknown> {
    const url = `${environment.apiUrl}/business/account/password`;
    return this.http.patch<unknown>(url, body);
  }

  isLoggedIn(): boolean {
    return !!this.getToken();
  }

  /** Post-login maestra: panel o elegir empresa; la cuenta se carga en el shell del panel. */
  navigateAfterMasterLogin(): Observable<void> {
    if (this.needsMasterCompanySelection()) {
      this.enterMasterCompanyChooser();
    } else {
      this.enterBusinessPanel();
    }
    return of(undefined);
  }
}

function extractMasterCompaniesFromLoginResponse(
  res: BusinessMasterLoginResponse,
): BusinessMasterCompanySummary[] {
  const top = normalizeMasterCompanies(res.companies);
  if (top.length > 0) {
    return top;
  }
  const nested = res.masterAccount as { companies?: unknown } | undefined;
  return normalizeMasterCompanies(nested?.companies);
}

function normalizeMasterCompanies(raw: unknown): BusinessMasterCompanySummary[] {
  if (!Array.isArray(raw)) return [];
  const out: BusinessMasterCompanySummary[] = [];
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue;
    const o = row as Record<string, unknown>;
    const id = typeof o['id'] === 'string' ? o['id'] : '';
    if (!id) continue;
    out.push({
      id,
      tradeName: typeof o['tradeName'] === 'string' ? o['tradeName'] : undefined,
      legalName: typeof o['legalName'] === 'string' ? o['legalName'] : undefined,
      rfc: typeof o['rfc'] === 'string' ? o['rfc'] : undefined,
      abbreviation: typeof o['abbreviation'] === 'string' ? o['abbreviation'] : undefined,
    });
  }
  return out;
}
